/* VastraGharr Luxe 3.0 - header behaviours
   - <vg-announcement>: rotating announcement messages (fade / slide, swipe, pause on hover/focus/off-screen)
   - <vg-priority-nav>: keeps the desktop menu on one line, moving overflow links into "More";
     hover intent (open / switch / close delays + soft close) and arrow-key navigation
   - Menu drawer helpers: VG.openMenu(), VG.closeMenu(), [data-vg-menu-close]
   - Footer: animated accordions (mobile) and newsletter validation / sending state
*/
(function () {
  'use strict';

  window.VG = window.VG || {};
  if (window.VG.headerScriptLoaded) return;
  window.VG.headerScriptLoaded = true;

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------- Announcement bar ---------- */
  if (!customElements.get('vg-announcement')) {
    customElements.define(
      'vg-announcement',
      class VgAnnouncement extends HTMLElement {
        connectedCallback() {
          this.slides = Array.prototype.slice.call(this.querySelectorAll('[data-vg-announce-slide]'));
          if (this.slides.length < 2) return;

          this.viewport = this.querySelector('.vg-announce__viewport');
          this.index = Math.max(
            0,
            this.slides.findIndex(function (slide) {
              return slide.classList.contains('is-active');
            })
          );
          this.autoplay = this.dataset.autoplay === 'true';
          this.speed = Math.max(3, parseInt(this.dataset.speed, 10) || 5) * 1000;
          this.hoverPaused = false;
          this.focusPaused = false;
          this.editorPaused = false;

          if (this.autoplay && this.viewport) this.viewport.setAttribute('aria-live', 'off');

          this.onPrev = this.go.bind(this, -1, true);
          this.onNext = this.go.bind(this, 1, true);
          this.prevButton = this.querySelector('[data-vg-announce-prev]');
          this.nextButton = this.querySelector('[data-vg-announce-next]');
          if (this.prevButton) this.prevButton.addEventListener('click', this.onPrev);
          if (this.nextButton) this.nextButton.addEventListener('click', this.onNext);

          this.onEnter = () => {
            this.hoverPaused = true;
            this.stop();
          };
          this.onLeave = () => {
            this.hoverPaused = false;
            this.start();
          };
          this.onFocusIn = () => {
            this.focusPaused = true;
            this.stop();
          };
          this.onFocusOut = (event) => {
            if (event.relatedTarget && this.contains(event.relatedTarget)) return;
            this.focusPaused = false;
            this.start();
          };
          this.addEventListener('mouseenter', this.onEnter);
          this.addEventListener('mouseleave', this.onLeave);
          this.addEventListener('focusin', this.onFocusIn);
          this.addEventListener('focusout', this.onFocusOut);

          this.onTouchStart = (event) => {
            if (!event.touches || !event.touches.length) return;
            this.touchX = event.touches[0].clientX;
            this.touchY = event.touches[0].clientY;
          };
          this.onTouchEnd = (event) => {
            if (this.touchX === undefined || !event.changedTouches || !event.changedTouches.length) return;
            var dx = event.changedTouches[0].clientX - this.touchX;
            var dy = event.changedTouches[0].clientY - this.touchY;
            this.touchX = undefined;
            if (Math.abs(dx) < 36 || Math.abs(dx) < Math.abs(dy)) return;
            this.go(dx < 0 ? 1 : -1, true);
          };
          this.addEventListener('touchstart', this.onTouchStart, { passive: true });
          this.addEventListener('touchend', this.onTouchEnd, { passive: true });

          this.onVisibility = () => {
            if (document.hidden) this.stop();
            else this.start();
          };
          document.addEventListener('visibilitychange', this.onVisibility);

          this.onBlockSelect = (event) => {
            var slide = event.target && event.target.closest ? event.target.closest('[data-vg-announce-slide]') : null;
            if (!slide || !this.contains(slide)) return;
            this.editorPaused = true;
            this.stop();
            var target = this.slides.indexOf(slide);
            if (target > -1 && target !== this.index) this.show(target, target > this.index ? 'next' : 'prev', false);
          };
          this.onBlockDeselect = (event) => {
            if (!event.target || !this.contains(event.target)) return;
            this.editorPaused = false;
            this.start();
          };
          document.addEventListener('shopify:block:select', this.onBlockSelect);
          document.addEventListener('shopify:block:deselect', this.onBlockDeselect);

          // Rotation pauses while the bar is scrolled out of view (it is not part of the sticky header).
          this.offscreen = false;
          if ('IntersectionObserver' in window) {
            this.visibilityObserver = new IntersectionObserver((entries) => {
              var entry = entries[entries.length - 1];
              this.offscreen = !entry.isIntersecting;
              if (this.offscreen) this.stop();
              else this.start();
            });
            this.visibilityObserver.observe(this);
          }

          this.start();
        }

        disconnectedCallback() {
          this.stop();
          if (this.visibilityObserver) this.visibilityObserver.disconnect();
          // connectedCallback re-binds these on reconnect (the theme editor moves sections on reorder).
          if (this.prevButton && this.onPrev) this.prevButton.removeEventListener('click', this.onPrev);
          if (this.nextButton && this.onNext) this.nextButton.removeEventListener('click', this.onNext);
          if (this.onEnter) {
            this.removeEventListener('mouseenter', this.onEnter);
            this.removeEventListener('mouseleave', this.onLeave);
            this.removeEventListener('focusin', this.onFocusIn);
            this.removeEventListener('focusout', this.onFocusOut);
            this.removeEventListener('touchstart', this.onTouchStart);
            this.removeEventListener('touchend', this.onTouchEnd);
          }
          if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
          if (this.onBlockSelect) document.removeEventListener('shopify:block:select', this.onBlockSelect);
          if (this.onBlockDeselect) document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
        }

        start() {
          this.stop();
          if (!this.autoplay || this.hoverPaused || this.focusPaused || this.editorPaused || this.offscreen || document.hidden)
            return;
          this.timer = window.setTimeout(() => {
            this.go(1, false);
            this.start();
          }, this.speed);
        }

        stop() {
          if (this.timer) window.clearTimeout(this.timer);
          this.timer = null;
        }

        go(step, manual) {
          var count = this.slides.length;
          var target = (this.index + step + count) % count;
          this.show(target, step > 0 ? 'next' : 'prev', manual);
          if (manual) this.start();
        }

        show(target, direction, manual) {
          if (target === this.index) return;
          var current = this.slides[this.index];
          var incoming = this.slides[target];

          if (this.viewport) this.viewport.setAttribute('aria-live', manual ? 'polite' : 'off');

          this.dataset.dir = direction;
          this.slides.forEach(function (slide) {
            if (slide !== current && slide !== incoming) slide.classList.remove('is-leaving');
          });
          incoming.classList.remove('is-leaving');
          // Flush styles so the incoming slide snaps to its starting side before animating in.
          void incoming.offsetWidth;

          current.classList.remove('is-active');
          current.classList.add('is-leaving');
          current.setAttribute('aria-hidden', 'true');
          current.querySelectorAll('a').forEach(function (link) {
            link.setAttribute('tabindex', '-1');
          });

          incoming.classList.add('is-active');
          incoming.removeAttribute('aria-hidden');
          incoming.querySelectorAll('a').forEach(function (link) {
            link.removeAttribute('tabindex');
          });

          window.clearTimeout(current.vgLeaveTimer);
          current.vgLeaveTimer = window.setTimeout(
            function () {
              current.classList.remove('is-leaving');
            },
            reducedMotion.matches ? 260 : 650
          );

          this.index = target;
        }
      }
    );
  }

  /* ---------- Priority navigation ("More") ---------- */
  if (!customElements.get('vg-priority-nav')) {
    customElements.define(
      'vg-priority-nav',
      class VgPriorityNav extends HTMLElement {
        connectedCallback() {
          this.list = this.querySelector('[data-vg-nav-list]');
          if (!this.list) return;
          if (this.dataset.hover === 'true' && !this.hoverReady) {
            this.hoverReady = true;
            this.initHover();
          }
          if (!this.keyboardReady) {
            this.keyboardReady = true;
            this.initKeyboard();
          }
          this.items = Array.prototype.slice.call(this.list.querySelectorAll(':scope > [data-vg-nav-item]'));
          this.more = this.list.querySelector(':scope > [data-vg-nav-more]');
          if (!this.more || this.items.length < 2) return;

          this.moreEntries = Array.prototype.slice.call(this.more.querySelectorAll('[data-vg-more-index]'));
          this.widths = null;
          this.classList.add('vg-nav--ready');

          this.scheduleUpdate = () => {
            window.cancelAnimationFrame(this.frame);
            this.frame = window.requestAnimationFrame(() => this.update());
          };

          if ('ResizeObserver' in window) {
            this.resizeObserver = new ResizeObserver(this.scheduleUpdate);
            this.resizeObserver.observe(this);
          } else {
            window.addEventListener('resize', this.scheduleUpdate);
          }

          if (document.fonts && document.fonts.ready) {
            document.fonts.ready.then(() => {
              this.widths = null;
              this.scheduleUpdate();
            });
          }

          this.update();
        }

        /* Hover intent for desktop menus: a short delay before the first open, a slightly longer one before
           switching (so a diagonal move towards an open panel does not flip menus), a forgiving close delay
           and a short fade-out. Keyboard and click toggling stay with Dawn's header-menu. */
        initHover() {
          var hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 990px)');
          var OPEN_DELAY = 110;
          var SWITCH_DELAY = 160;
          var CLOSE_DELAY = 260;
          var CLOSE_ANIMATION = 200;
          var entries = [];

          var closeNow = function (entry) {
            window.clearTimeout(entry.animationTimer);
            entry.details.classList.remove('is-closing');
            if (entry.details.hasAttribute('open')) entry.details.removeAttribute('open');
            entry.summary.setAttribute('aria-expanded', 'false');
          };

          var closeSoftly = function (entry) {
            if (!entry.details.hasAttribute('open') || entry.details.classList.contains('is-closing')) return;
            if (reducedMotion.matches || entry.details.contains(document.activeElement)) {
              closeNow(entry);
              return;
            }
            entry.details.classList.add('is-closing');
            entry.summary.setAttribute('aria-expanded', 'false');
            entry.animationTimer = window.setTimeout(function () {
              entry.details.classList.remove('is-closing');
              entry.details.removeAttribute('open');
            }, CLOSE_ANIMATION);
          };

          var isShowing = function (entry) {
            return entry.details.hasAttribute('open') && !entry.details.classList.contains('is-closing');
          };

          var openEntry = function (entry, switching) {
            entries.forEach(function (other) {
              if (other === entry) return;
              window.clearTimeout(other.openTimer);
              window.clearTimeout(other.closeTimer);
              if (other.details.hasAttribute('open')) closeNow(other);
            });
            window.clearTimeout(entry.animationTimer);
            entry.details.classList.remove('is-closing');
            entry.details.classList.toggle('is-switching', !!switching);
            if (!entry.details.hasAttribute('open')) entry.details.setAttribute('open', '');
            entry.summary.setAttribute('aria-expanded', 'true');
          };

          this.querySelectorAll('[data-vg-nav-list] > li > header-menu').forEach(function (menu) {
            var details = menu.querySelector('details');
            var summary = details && details.querySelector('summary');
            if (!summary) return;
            var entry = { menu: menu, details: details, summary: summary };
            entries.push(entry);

            menu.addEventListener('mouseenter', function () {
              if (!hoverQuery.matches) return;
              window.clearTimeout(entry.closeTimer);
              if (isShowing(entry)) return;
              var switching = entries.some(function (other) {
                return other !== entry && isShowing(other);
              });
              window.clearTimeout(entry.openTimer);
              entry.openTimer = window.setTimeout(function () {
                openEntry(entry, switching);
              }, switching ? SWITCH_DELAY : OPEN_DELAY);
            });

            menu.addEventListener('mouseleave', function () {
              if (!hoverQuery.matches) return;
              window.clearTimeout(entry.openTimer);
              window.clearTimeout(entry.closeTimer);
              entry.closeTimer = window.setTimeout(function () {
                closeSoftly(entry);
              }, CLOSE_DELAY);
            });

            // A mouse click on an already hover-opened menu keeps it open; keyboard toggling is untouched.
            summary.addEventListener('click', function (event) {
              if (!hoverQuery.matches || event.detail === 0) return;
              if (!details.hasAttribute('open') || details.classList.contains('is-closing')) return;
              event.preventDefault();
              // global.js has already flipped aria-expanded to "false" for this click; the menu stays open.
              summary.setAttribute('aria-expanded', 'true');
            });

            details.addEventListener('toggle', function () {
              if (!details.hasAttribute('open')) details.classList.remove('is-closing', 'is-switching');
            });
          });

          this.hoverEntries = entries;
        }

        /* Arrow keys: Left/Right move between top-level items, Down opens a menu and enters it,
           Up/Down move through its links, Home/End jump. Escape stays with Dawn (closes and refocuses). */
        initKeyboard() {
          var nav = this;
          this.list.addEventListener('keydown', function (event) {
            var key = event.key;
            if (['ArrowLeft', 'ArrowRight', 'ArrowDown', 'ArrowUp', 'Home', 'End'].indexOf(key) === -1) return;
            if (event.altKey || event.ctrlKey || event.metaKey) return;
            var current = document.activeElement;
            var top = nav.topLevelTargets();
            var topIndex = top.indexOf(current);

            if (topIndex > -1) {
              var target = null;
              if (key === 'ArrowRight') target = top[(topIndex + 1) % top.length];
              else if (key === 'ArrowLeft') target = top[(topIndex - 1 + top.length) % top.length];
              else if (key === 'Home') target = top[0];
              else if (key === 'End') target = top[top.length - 1];
              if (target) {
                event.preventDefault();
                nav.closeMenus();
                target.focus();
                return;
              }
              if (current.tagName === 'SUMMARY' && (key === 'ArrowDown' || key === 'ArrowUp')) {
                event.preventDefault();
                var details = current.parentElement;
                if (!details.hasAttribute('open')) current.click();
                window.setTimeout(function () {
                  var links = nav.panelLinks(details);
                  if (links.length) (key === 'ArrowUp' ? links[links.length - 1] : links[0]).focus();
                }, 30);
              }
              return;
            }

            var openDetails =
              current && current.closest ? current.closest('[data-vg-nav-list] > li > header-menu > details[open]') : null;
            if (!openDetails) return;
            var links = nav.panelLinks(openDetails);
            var index = links.indexOf(current);
            var summary = openDetails.querySelector('summary');
            if (index === -1) return;
            event.preventDefault();
            if (key === 'ArrowDown') links[(index + 1) % links.length].focus();
            else if (key === 'ArrowUp') (index === 0 ? summary : links[index - 1]).focus();
            else if (key === 'Home') links[0].focus();
            else if (key === 'End') links[links.length - 1].focus();
            else {
              var position = top.indexOf(summary);
              var next = top[(position + (key === 'ArrowRight' ? 1 : -1) + top.length) % top.length];
              nav.closeMenus();
              if (next) next.focus();
            }
          });
        }

        topLevelTargets() {
          return Array.prototype.slice
            .call(this.list.querySelectorAll(':scope > li:not([hidden]):not(.is-overflow)'))
            .map(function (item) {
              return item.querySelector(':scope > header-menu > details > summary, :scope > a');
            })
            .filter(function (el) {
              return el && el.getClientRects().length;
            });
        }

        panelLinks(details) {
          var summary = details.querySelector('summary');
          var panel = summary ? summary.nextElementSibling : null;
          if (!panel) return [];
          return Array.prototype.slice.call(panel.querySelectorAll('a[href], summary')).filter(function (el) {
            return !el.closest('[hidden]') && el.getClientRects().length;
          });
        }

        closeMenus() {
          this.list.querySelectorAll(':scope > li > header-menu > details[open]').forEach(function (details) {
            details.classList.remove('is-closing');
            details.removeAttribute('open');
            var summary = details.querySelector('summary');
            if (summary) summary.setAttribute('aria-expanded', 'false');
          });
        }

        disconnectedCallback() {
          if (this.resizeObserver) this.resizeObserver.disconnect();
          window.removeEventListener('resize', this.scheduleUpdate);
          window.cancelAnimationFrame(this.frame);
        }

        measure() {
          this.items.forEach(function (item) {
            item.classList.remove('is-overflow');
          });
          this.more.hidden = false;
          this.widths = this.items.map(function (item) {
            return item.getBoundingClientRect().width;
          });
          this.moreWidth = this.more.getBoundingClientRect().width;
        }

        update() {
          var available = this.clientWidth;
          if (!available) return;

          var needsMeasure =
            !this.widths ||
            this.widths.every(function (width) {
              return width === 0;
            });
          if (needsMeasure) this.measure();

          var total = this.widths.reduce(function (sum, width) {
            return sum + width;
          }, 0);
          var visible = this.items.length;

          if (total > available + 1) {
            var used = this.moreWidth;
            visible = 0;
            for (var i = 0; i < this.widths.length; i++) {
              if (used + this.widths[i] > available) break;
              used += this.widths[i];
              visible++;
            }
          }

          this.items.forEach(function (item, index) {
            item.classList.toggle('is-overflow', index >= visible);
          });
          this.more.hidden = visible >= this.items.length;
          this.moreEntries.forEach(function (entry) {
            entry.hidden = Number(entry.dataset.vgMoreIndex) < visible;
          });
        }
      }
    );
  }

  /* ---------- Footer accordions (mobile): animated open / close ---------- */
  if (!customElements.get('vg-accordion')) {
    customElements.define(
      'vg-accordion',
      class VgAccordion extends HTMLElement {
        connectedCallback() {
          this.details = this.querySelector('details');
          this.summary = this.details && this.details.querySelector('summary');
          this.content = this.summary && this.summary.nextElementSibling;
          if (!this.content || this.ready) return;
          this.ready = true;
          this.mobile = window.matchMedia(this.dataset.media || '(max-width: 749px)');
          this.onClick = this.onClick.bind(this);
          this.onChange = this.sync.bind(this);
          this.summary.addEventListener('click', this.onClick);
          if (this.mobile.addEventListener) this.mobile.addEventListener('change', this.onChange);
          else if (this.mobile.addListener) this.mobile.addListener(this.onChange);
          this.sync();
        }

        disconnectedCallback() {
          if (!this.ready) return;
          this.ready = false;
          this.summary.removeEventListener('click', this.onClick);
          if (this.mobile.removeEventListener) this.mobile.removeEventListener('change', this.onChange);
          else if (this.mobile.removeListener) this.mobile.removeListener(this.onChange);
          if (this.animation) this.animation.cancel();
        }

        /* Columns are always open on tablet / desktop; on phones they start closed. */
        sync() {
          if (this.animation) this.animation.cancel();
          this.details.classList.remove('is-closing');
          if (this.mobile.matches) {
            if (!this.touched) this.details.removeAttribute('open');
          } else {
            this.details.setAttribute('open', '');
          }
          this.summary.setAttribute('aria-expanded', this.details.hasAttribute('open') ? 'true' : 'false');
        }

        onClick(event) {
          event.preventDefault();
          if (!this.mobile.matches) return;
          this.touched = true;
          var opening = !this.details.hasAttribute('open') || this.details.classList.contains('is-closing');
          if (opening) this.expand();
          else this.collapse();
        }

        animate(from, to, done) {
          if (this.animation) this.animation.cancel();
          if (reducedMotion.matches || typeof this.content.animate !== 'function') {
            done();
            return;
          }
          // Padding collapses with the height (border-box), so the panel closes fully instead of stopping at its padding.
          var styles = window.getComputedStyle(this.content);
          var openFrame = { paddingTop: styles.paddingTop, paddingBottom: styles.paddingBottom };
          var frame = function (height) {
            var result = { height: height + 'px', opacity: height ? 1 : 0 };
            result.paddingTop = height ? openFrame.paddingTop : '0px';
            result.paddingBottom = height ? openFrame.paddingBottom : '0px';
            return result;
          };
          this.content.style.overflow = 'hidden';
          this.animation = this.content.animate([frame(from), frame(to)], {
            duration: to ? 380 : 300,
            easing: 'cubic-bezier(0.22, 0.61, 0.36, 1)',
          });
          this.animation.onfinish = () => {
            this.animation = null;
            this.content.style.overflow = '';
            done();
          };
        }

        expand() {
          var start = this.details.classList.contains('is-closing') ? this.content.getBoundingClientRect().height : 0;
          this.details.classList.remove('is-closing');
          this.details.setAttribute('open', '');
          this.summary.setAttribute('aria-expanded', 'true');
          var end = this.content.scrollHeight;
          this.animate(start, end, function () {});
        }

        collapse() {
          var start = this.content.getBoundingClientRect().height;
          this.details.classList.add('is-closing');
          this.summary.setAttribute('aria-expanded', 'false');
          this.animate(start, 0, () => {
            this.details.classList.remove('is-closing');
            this.details.removeAttribute('open');
          });
        }
      }
    );
  }

  /* ---------- Footer newsletter: inline validation + sending state ----------
     Native constraint validation stays on (Shopify's form protection submits the form itself, so an
     invalid address must never reach the submit event). The browser bubble is replaced by an inline,
     announced message; a stricter address check is applied through setCustomValidity. */
  var EMAIL_PATTERN = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/;

  if (!customElements.get('vg-newsletter')) {
    customElements.define(
      'vg-newsletter',
      class VgNewsletter extends HTMLElement {
        connectedCallback() {
          this.form = this.querySelector('form');
          this.input = this.form && this.form.querySelector('input[type="email"]');
          this.button = this.form && this.form.querySelector('button[type="submit"]');
          if (!this.input || this.ready) return;
          this.ready = true;
          this.errorId = (this.input.id || 'VgNewsletter') + '-client-error';
          this.onSubmit = this.onSubmit.bind(this);
          this.onInput = this.onInput.bind(this);
          this.onInvalid = this.onInvalid.bind(this);
          this.onPageShow = this.reset.bind(this);
          this.form.addEventListener('submit', this.onSubmit);
          this.input.addEventListener('input', this.onInput);
          this.input.addEventListener('change', this.onInput);
          this.input.addEventListener('invalid', this.onInvalid);
          window.addEventListener('pageshow', this.onPageShow);
          this.syncValidity();
        }

        disconnectedCallback() {
          if (!this.ready) return;
          this.ready = false;
          this.form.removeEventListener('submit', this.onSubmit);
          this.input.removeEventListener('input', this.onInput);
          this.input.removeEventListener('change', this.onInput);
          this.input.removeEventListener('invalid', this.onInvalid);
          window.removeEventListener('pageshow', this.onPageShow);
        }

        value() {
          return this.input.value.trim();
        }

        syncValidity() {
          var value = this.value();
          this.input.setCustomValidity(!value || EMAIL_PATTERN.test(value) ? '' : 'invalid');
        }

        errorElement() {
          var el = document.getElementById(this.errorId);
          if (!el) {
            el = document.createElement('p');
            el.id = this.errorId;
            el.className = 'vg-newsletter__error';
            el.setAttribute('role', 'alert');
            var wrapper = this.form.querySelector('.newsletter-form__field-wrapper') || this.form;
            wrapper.appendChild(el);
          }
          return el;
        }

        showError(message) {
          var el = this.errorElement();
          el.textContent = message;
          el.hidden = false;
          this.classList.add('has-error');
          this.input.setAttribute('aria-invalid', 'true');
          var described = (this.input.getAttribute('aria-describedby') || '').split(' ').filter(Boolean);
          if (described.indexOf(this.errorId) === -1) described.push(this.errorId);
          this.input.setAttribute('aria-describedby', described.join(' '));
        }

        clearError() {
          var el = document.getElementById(this.errorId);
          if (el) {
            el.textContent = '';
            el.hidden = true;
          }
          this.classList.remove('has-error');
          if (!this.querySelector('#ContactFooter-error')) this.input.removeAttribute('aria-invalid');
          var described = (this.input.getAttribute('aria-describedby') || '').split(' ').filter((id) => {
            return id && id !== this.errorId;
          });
          if (described.length) this.input.setAttribute('aria-describedby', described.join(' '));
          else this.input.removeAttribute('aria-describedby');
        }

        onInput() {
          this.syncValidity();
          if (this.classList.contains('has-error') && this.input.checkValidity()) this.clearError();
        }

        onInvalid(event) {
          event.preventDefault();
          this.showError(
            this.value() ? 'Please enter a valid email address, like name@example.com.' : 'Please enter your email address.'
          );
          this.input.focus();
        }

        onSubmit(event) {
          if (this.sending) {
            event.preventDefault();
            return;
          }
          this.clearError();
          this.sending = true;
          this.classList.add('is-sending');
          if (this.button) {
            this.button.classList.add('is-loading');
            this.button.setAttribute('aria-disabled', 'true');
          }
        }

        reset(event) {
          if (event && !event.persisted) return;
          this.sending = false;
          this.classList.remove('is-sending');
          if (this.button) {
            this.button.classList.remove('is-loading');
            this.button.removeAttribute('aria-disabled');
          }
        }
      }
    );
  }

  /* ---------- Menu drawer helpers ---------- */
  function menuSummary() {
    return document.querySelector('#Details-menu-drawer-container > summary');
  }

  window.VG.openMenu = function () {
    var summary = menuSummary();
    if (summary && !summary.parentElement.hasAttribute('open')) summary.click();
  };

  window.VG.closeMenu = function () {
    var summary = menuSummary();
    if (!summary) return;
    var details = summary.parentElement;
    if (!details.hasAttribute('open') || !details.classList.contains('menu-opening')) return;
    var drawer = summary.closest('header-drawer');
    if (drawer && typeof drawer.closeMenuDrawer === 'function') {
      drawer.closeMenuDrawer(new Event('click'), summary);
    } else {
      summary.click();
    }
    summary.setAttribute('aria-expanded', 'false');
  };

  // Capture phase so the drawer closes before other handlers (e.g. the wishlist drawer) move focus.
  document.addEventListener(
    'click',
    function (event) {
      var closer = event.target.closest('[data-vg-menu-close]');
      if (closer) {
        window.VG.closeMenu();
      }
    },
    true
  );
})();
